import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ImportProgress {
  id: string;
  status: string;
  current_page: number;
  total_pages: number;
  vehicles_found: number;
  vehicles_enriched: number;
  vehicles_imported: number;
  current_step: string;
  error_message: string | null;
  started_at: string;
  updated_at: string;
}

export const useImportProgress = (isImporting: boolean) => {
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLatestProgress = useCallback(async () => {
    const { data, error } = await supabase
      .from('import_progress')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(1)
      .single();

    if (!error && data) {
      setProgress(data as unknown as ImportProgress);
    }
  }, []);

  useEffect(() => {
    if (isImporting) {
      // Poll every 2 seconds
      fetchLatestProgress();
      intervalRef.current = setInterval(fetchLatestProgress, 2000);
    } else {
      // One final fetch then stop
      fetchLatestProgress();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isImporting, fetchLatestProgress]);

  return progress;
};
